import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Zap, Droplet, ChevronRight, ChevronDown, Download, Trash2, Loader2,
  FolderOpen, Folder, Square, CheckSquare2,
} from 'lucide-react';
import { formatNumber, formatPeriod, formatEuro } from '../../utils/formatters';
import { FolderSearchInput } from './BillsList';

/**
 * Storico analisi raggruppate per lead source, con selezione batch.
 * @param {Object} props
 * @param {Array} props.groupedAnalyses - [{key, label, analyses}]
 * @param {Object} props.openFolders - mappa {key: boolean}
 * @param {Function} props.onToggleFolder - (key) => void
 * @param {Set<string>} props.selectedIds - ID selezionati per batch
 * @param {Function} props.onToggleSelection - (id, event) => void
 * @param {Function} props.onToggleSelectAll - (analyses) => void
 * @param {Object|null} props.currentAnalysis - analisi attualmente visualizzata
 * @param {boolean} props.isHistorical
 * @param {Function} props.onViewAnalysis - (analysis) => void
 * @param {Function} props.onBatchPdf
 * @param {Function} props.onBatchDelete
 * @param {boolean} props.batchDownloading
 * @param {boolean} props.batchDeleting
 * @param {number} props.totalCount
 */
export const HistoricalAnalyses = ({
  groupedAnalyses, openFolders, onToggleFolder,
  selectedIds, onToggleSelection, onToggleSelectAll,
  currentAnalysis, isHistorical,
  onViewAnalysis,
  onBatchPdf, onBatchDelete,
  batchDownloading, batchDeleting,
  totalCount,
}) => {
  if (totalCount === 0) return null;

  return (
    <div className="el-card p-6" data-testid="history-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="el-label">Storico Analisi ({totalCount})</h3>
        {selectedIds.size > 0 && (
          <BatchActions
            count={selectedIds.size}
            onPdf={onBatchPdf}
            onDelete={onBatchDelete}
            downloading={batchDownloading}
            deleting={batchDeleting}
          />
        )}
      </div>
      <div className="space-y-2">
        {groupedAnalyses.map((group) => (
          <AnalysisFolder
            key={`hist-${group.key}`}
            group={group}
            isOpen={!!openFolders[group.key]}
            onToggle={() => onToggleFolder(group.key)}
            selectedIds={selectedIds}
            onToggleSelection={onToggleSelection}
            onToggleSelectAll={onToggleSelectAll}
            currentAnalysis={currentAnalysis}
            isHistorical={isHistorical}
            onViewAnalysis={onViewAnalysis}
          />
        ))}
      </div>
    </div>
  );
};

/** Pulsanti azioni batch */
const BatchActions = ({ count, onPdf, onDelete, downloading, deleting }) => (
  <div className="flex items-center gap-2">
    <span className="text-xs text-[#5C6E5C]">{count} selezionate</span>
    <button
      onClick={onPdf}
      disabled={downloading}
      className="flex items-center gap-1 py-1 px-3 rounded-lg text-xs font-medium bg-[#4CAF50] text-white hover:bg-[#3D8C40] disabled:opacity-50 transition-colors"
      data-testid="batch-pdf-btn"
    >
      {downloading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
      PDF
    </button>
    <button
      onClick={onDelete}
      disabled={deleting}
      className="flex items-center gap-1 py-1 px-3 rounded-lg text-xs font-medium bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 transition-colors"
      data-testid="batch-delete-btn"
    >
      {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
      Elimina
    </button>
  </div>
);

/** Cartella singola dello storico */
const AnalysisFolder = ({
  group, isOpen, onToggle,
  selectedIds, onToggleSelection, onToggleSelectAll,
  currentAnalysis, isHistorical, onViewAnalysis,
}) => {
  const [query, setQuery] = useState('');

  const filteredAnalyses = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return group.analyses;
    return group.analyses.filter((a) => {
      const cd = a.competitor_data || {};
      const name = (cd.client_name || '').toLowerCase();
      const sii = (cd.sii_code || '').toLowerCase();
      return name.includes(q) || sii.includes(q);
    });
  }, [group.analyses, query]);

  const filteredGroupIds = filteredAnalyses.map((a) => a.id);
  const allSelected = filteredGroupIds.length > 0 && filteredGroupIds.every((id) => selectedIds.has(id));

  return (
    <div className="border border-[#DCE3DC] rounded-xl overflow-hidden" data-testid={`history-folder-${group.key}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-3 bg-[#F4F7F4] hover:bg-[#E8F5E9] transition-colors text-left"
        data-testid={`history-folder-toggle-${group.key}`}
      >
        {isOpen ? <FolderOpen className="w-5 h-5 text-[#4CAF50] flex-shrink-0" /> : <Folder className="w-5 h-5 text-[#5C6E5C] flex-shrink-0" />}
        <span className="font-medium text-sm text-[#1B291B] flex-1">{group.label}</span>
        <span className="text-xs text-[#5C6E5C] bg-white px-2 py-0.5 rounded-full border border-[#DCE3DC]">{group.analyses.length}</span>
        <ChevronDown className={`w-4 h-4 text-[#5C6E5C] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-2 space-y-2 bg-white">
              {group.analyses.length > 3 && (
                <FolderSearchInput
                  value={query}
                  onChange={setQuery}
                  placeholder="Cerca per nome cliente o POD/PDR..."
                  testId={`history-search-${group.key}`}
                />
              )}
              <button
                onClick={() => onToggleSelectAll(filteredAnalyses)}
                className="flex items-center gap-2 w-full p-2 rounded-lg hover:bg-[#F4F7F4] transition-colors text-left"
                data-testid={`history-select-all-${group.key}`}
              >
                {allSelected ? <CheckSquare2 className="w-4 h-4 text-[#4CAF50]" /> : <Square className="w-4 h-4 text-[#5C6E5C]" />}
                <span className="text-xs font-medium text-[#5C6E5C]">{allSelected ? 'Deseleziona tutto' : 'Seleziona tutto'}</span>
              </button>

              {filteredAnalyses.length === 0 ? (
                <p className="text-xs text-[#5C6E5C] text-center py-3 italic">
                  Nessuna analisi corrisponde a &ldquo;{query}&rdquo;
                </p>
              ) : (
                filteredAnalyses.map((a) => (
                  <AnalysisHistoryItem
                    key={a.id}
                    analysis={a}
                    isSelected={selectedIds.has(a.id)}
                    isViewing={currentAnalysis?.id === a.id && isHistorical}
                    onToggleSelection={onToggleSelection}
                    onView={onViewAnalysis}
                  />
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/** Singola analisi nello storico */
const AnalysisHistoryItem = ({ analysis: a, isSelected, isViewing, onToggleSelection, onView }) => (
  <div
    className={`p-3 rounded-lg border transition-all cursor-pointer ${
      isSelected ? 'border-[#4CAF50] bg-[#E8F5E9]'
        : isViewing ? 'border-[#4CAF50] bg-[#E8F5E9]/50'
        : 'border-[#DCE3DC] hover:border-[#4CAF50]/50'
    }`}
    data-testid={`history-item-${a.id}`}
  >
    <div className="flex items-start gap-2">
      <button
        onClick={(e) => onToggleSelection(a.id, e)}
        className="mt-0.5 flex-shrink-0"
        data-testid={`history-check-${a.id}`}
      >
        {isSelected ? <CheckSquare2 className="w-4 h-4 text-[#4CAF50]" /> : <Square className="w-4 h-4 text-[#DCE3DC] hover:text-[#5C6E5C]" />}
      </button>

      <div className="flex-1" onClick={() => onView(a)}>
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            {a.competitor_data?.service_type === 'gas' ? (
              <Droplet className="w-5 h-5 text-sky-400 fill-sky-400" />
            ) : (
              <Zap className="w-5 h-5 text-yellow-500" />
            )}
            <div>
              <p className="font-medium text-[#1B291B] text-sm">{a.competitor_data?.client_name || 'N/A'}</p>
              <p className="text-xs text-[#5C6E5C] mt-0.5">vs {(a.competitor_data?.supplier_name || '').toUpperCase()}</p>
              <p className="text-xs text-[#5C6E5C] mt-0.5">
                {a.competitor_data?.consumption ? formatNumber(a.competitor_data.consumption, 0) : '?'} {a.competitor_data?.consumption_unit || ''}
                {a.competitor_data?.billing_period_start && (
                  <> &bull; {formatPeriod(a.competitor_data.billing_period_start, a.competitor_data.billing_period_end)}</>
                )}
                {a.competitor_data?.sii_code && (
                  <> &bull; <span className="font-medium">{a.competitor_data?.service_type === 'gas' ? 'PDR' : 'POD'}:</span> {a.competitor_data.sii_code}</>
                )}
              </p>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="text-[10px] bg-[#F4F7F4] text-[#5C6E5C] px-1.5 py-0.5 rounded">{a.selected_cte}</span>
                {(() => {
                  const isAnnual = !!a.annual_projection;
                  const positive = isAnnual ? a.annual_projection.is_positive : a.is_positive;
                  const pct = isAnnual ? a.annual_projection.savings_annual_percent : a.savings_percent;
                  const amt = isAnnual ? a.annual_projection.savings_annual : a.savings_amount;
                  const suffix = isAnnual ? '/anno' : '';
                  const sign = positive ? '-' : '+';
                  return (
                    <>
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${positive ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                        {sign}{formatNumber(Math.abs(pct), 1)}%{suffix}
                      </span>
                      {amt != null && (
                        <span className={`text-[10px] font-semibold ${positive ? 'text-green-700' : 'text-orange-700'}`}>
                          {sign}{formatEuro(Math.abs(amt))}{suffix}
                        </span>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-[#5C6E5C] mt-1" />
        </div>
      </div>
    </div>
  </div>
);
